import type {ActionListProps, ActionMenu, ActionMenuProps} from '@primer/react'
import {type ComponentProps, type ReactElement, useCallback, useState} from 'react'
import {MenuItemAnchor} from './MenuItemAnchor'
import {NestableActionMenuProvider, useNestableActionMenu} from './NestableActionMenuContext'
import {NestedSelectPanel} from './NestedSelectPanel'
import {RootMenu} from './RootMenu'

export type {MenuItemAnchorProps} from './MenuItemAnchor'
export type {NestedSelectPanelProps} from './NestedSelectPanel'

export interface NestableActionMenuProps extends ActionMenuProps {
  /**
   * The anchor for this menu. By default, this will be cloned and have accessible attributes & event handlers applied
   * automatically. To fully customize the anchor without any automatic wiring of props, explicitly set to `null` and
   * set the `anchorRef` instead, and be careful to correctly apply `aria-haspopup` and `aria-expanded`.
   *
   * For nested submenus, use `NestableActionMenu.MenuItemAnchor` as the anchor component. For the top-level menu,
   * this should almost always be a `Button` or `IconButton`.
   */
  anchor: ReactElement
  /** Optional override props for the underlying `Overlay`. */
  overlayProps?: Partial<ComponentProps<(typeof ActionMenu)['Overlay']>>
  /** Optional override props for the underlying `ActionList` within the `Overlay`. */
  actionListProps?: Partial<ActionListProps>
  /**
   * Contents of the menu. These should be:
   *
   *   - Valid `ActionList` children: `ActionList.Item`, `ActionList.Divider`, or `ActionList.Group`
   *   - Another `NestableActionMenu`, typically with `NestableActionMenu.MenuItemAnchor` as its anchor
   *   - `NestableActionMenu.NestedSelectPanel`, typically with `NestableActionMenu.MenuItemAnchor` as its anchor
   */
  children: ReactElement | ReactElement[]
}

/**
 * A variant of `ActionMenu` that can contain submenus. `NestableActionMenu` can be used inside of itself to form a
 * tree of menus. Can also contain `SelectPanel`s via the `NestableActionMenu.NestedSelectPanel` component.
 */
export const NestableActionMenu = Object.assign(
  function NestableActionMenu({
    anchor,
    overlayProps: {onKeyDown: externalOverlayOnKeyDown, ...overlayProps} = {},
    actionListProps,
    children,
    open: externalOpen,
    onOpenChange: externalOnOpenChange,
    ...actionMenuProps
  }: NestableActionMenuProps) {
    const [internalOpen, setInternalOpen] = useState(false)

    const setOpen = useCallback(
      (newOpen: boolean) => {
        externalOnOpenChange?.(newOpen)
        setInternalOpen(newOpen)
      },
      [externalOnOpenChange],
    )

    const parentContext = useNestableActionMenu()

    /** Close this menu and all menus above it. */
    const closeStack = useCallback(() => {
      setOpen(false)
      parentContext?.onCloseStack()
    }, [setOpen, parentContext])

    return (
      <NestableActionMenuProvider onCloseStack={closeStack}>
        <RootMenu
          anchor={anchor}
          isNested={parentContext !== null}
          open={externalOpen ?? internalOpen}
          setOpen={setOpen}
          actionListProps={actionListProps}
          actionMenuProps={actionMenuProps}
          overlayProps={overlayProps}
          closeStack={closeStack}
          externalOverlayOnKeyDown={externalOverlayOnKeyDown}
        >
          {children}
        </RootMenu>
      </NestableActionMenuProvider>
    )
  },
  {
    MenuItemAnchor,
    NestedSelectPanel,
  },
)

try{ NestableActionMenu.displayName ||= 'NestableActionMenu' } catch {}
try{ NestableActionMenu.displayName ||= 'NestableActionMenu' } catch {}